"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";

import { replayMetricsApi, parseAllTimeReplayMetrics, parseMatchNarrative, parseMatchReplayMetrics, parsePlayerReplayMetrics, parseTournamentGameScores, parseTournamentReplayMetrics } from "./validation";


/**
 * KAN-727: чтение поведенческих метрик по реплеям (эпик KAN-719).
 *
 * Маршрут — без префикса `/api`: Traefik срезает его до бэкенда, а базовый URL уже
 * зашит в клиент. Проксирующих роутов во `frontend/src/app/api/` не заводим — это
 * ломает контракт роутинга.
 */

/** Метрики всех игроков турнира. Пустой массив — реплеев ещё нет, это не ошибка. */
// KAN-993: опции отдельно от хука — по ним страница итогов прогревает кэш
// заранее, не дублируя ключ и загрузчик.
export const tournamentReplayMetricsQuery = (tournamentId: string) =>
  queryOptions({
    queryKey: ["replay-metrics", "tournament", tournamentId],
    queryFn: async () => parseTournamentReplayMetrics((await replayMetricsApi.getTournament(tournamentId)).data),
  });

export function useTournamentReplayMetrics(tournamentId: string | undefined) {
  return useQuery({ ...tournamentReplayMetricsQuery(tournamentId ?? ""), enabled: Boolean(tournamentId) });
}

/**
 * KAN-814: разбор матча — в том числе пары по ролям.
 *
 * ОТВЕТ — МАССИВ, ПО ЭЛЕМЕНТУ НА ИГРУ МАТЧА, а не один объект. Это выяснилось
 * только на живых данных: у матча Bo1 элемент один, у Bo3 их три, и каждый несёт
 * свои пары по ролям — состав линий между играми меняется. Первая версия хука
 * читала ответ как объект, `laneMatchups` оказывался `undefined`, и витрина молча
 * не рисовалась. Ни типы, ни тесты этого не ловили: тесты кормили компонент
 * напрямую, а тип был взят из имени DTO, а не из формы ответа.
 *
 * Отдельного запроса под пары не заводим: нарратив считается одним проходом, и
 * второй запрос за подмножеством того же ответа стоил бы дороже лишних полей в кэше.
 *
 * Пустой список — штатное состояние: реплеев у матча может не быть, а на арене пар
 * по ролям не существует как понятия (там больше двух сторон).
 */
export function useMatchNarrative(matchId: string | undefined) {
  return useQuery({
    queryKey: ["replay-metrics", "narrative", matchId],
    enabled: Boolean(matchId),
    queryFn: async () => {
      return parseMatchNarrative((await replayMetricsApi.getNarrative(matchId!)).data);
    },
  });
}

/**
 * KAN-876: балл за КАЖДУЮ игру турнира — одна строка на «игрок × игра».
 *
 * Это не тот же балл, что в агрегате: там игрок сравнивается с игроками роли за
 * турнир, здесь каждая игра — с такими же играми той же роли. Средний по играм не
 * обязан совпадать с агрегатом, и витрина обязана сказать это подписью.
 *
 * Пустой массив — реплеев нет либо ни одна игра не набрала группы сравнения. Не
 * ошибка: столбики просто не рисуются.
 *
 * Полная версия ответа весила ~1,7 МБ на турнир из 13 команд (строка метрик на каждую
 * игру), поэтому потребитель может отложить запрос через `enabled` (KAN-1166).
 */
export function useTournamentGameScores(
  tournamentId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ["replay-metrics", "tournament", tournamentId, "games"],
    enabled: Boolean(tournamentId) && (options.enabled ?? true),
    queryFn: async () => {
      // Лёгкая ручка: шесть полей столбика вместо полной строки метрик (~1,7 МБ → десятки КБ).
      return parseTournamentGameScores((await replayMetricsApi.getGameScores(tournamentId!)).data);
    },
  });
}

/**
 * Метрики одного игрока за турнир.
 *
 * «Игрока нет в данных турнира» — штатное состояние, и бэкенд отдаёт его как ПУСТОЙ
 * ОТВЕТ, а не 404 (KAN-1055): красный 404 в консоли на обычном сценарии создавал фон,
 * в котором терялась настоящая ошибка. `retry: false` оставлен: повторный запрос за
 * пустотой ничего не изменит, а 404 по несуществующему турниру ретраить тем более незачем.
 *
 * `Ok(null)` уезжает как 204 No Content; api-клиент отдаёт его как `null` (KAN-1180).
 */
export function usePlayerReplayMetrics(
  tournamentId: string | undefined,
  userId: string | undefined,
) {
  return useQuery({
    queryKey: ["replay-metrics", "player", tournamentId, userId],
    enabled: Boolean(tournamentId && userId),
    retry: false,
    queryFn: async () => {
      const data = (await replayMetricsApi.getPlayer(tournamentId!, userId!)).data;
      return data == null ? null : parsePlayerReplayMetrics(data);
    },
  });
}

/**
 * Метрики игрока за всё время — для профиля.
 *
 * Перцентилей в ответе НЕТ: они считаются внутри роли и внутри турнира, а здесь турниров
 * много. Витрина показывает абсолютные значения, и подпись не должна обещать сравнение.
 *
 * Нет ни одной разобранной игры — пустой ответ (204), а не 404: см. KAN-1055 и
 * KAN-1180: api-клиент отдаёт 204 как null.
 */
export function usePlayerAllTimeReplayMetrics(userId: string | undefined) {
  return useQuery({
    queryKey: ["replay-metrics", "player-all-time", userId],
    enabled: Boolean(userId),
    retry: false,
    queryFn: async () => {
      const data = (await replayMetricsApi.getAllTime(userId!)).data;
      return data == null ? null : parseAllTimeReplayMetrics(data);
    },
  });
}

/**
 * Метрики по матчу: строка на участника КАЖДОЙ игры серии.
 *
 * Перцентилей здесь нет — группа сравнения это турнир, а не матч. Витрина показывает
 * абсолютные значения.
 */
export function useMatchReplayMetrics(matchId: string | undefined) {
  return useQuery({
    queryKey: ["replay-metrics", "match", matchId],
    enabled: Boolean(matchId),
    queryFn: async () => {
      return parseMatchReplayMetrics((await replayMetricsApi.getMatch(matchId!)).data);
    },
  });
}
